/**
 * RichTextField — one language's version of an interface string, with the
 * formatting buttons a superadmin uses and a live preview of the result.
 *
 * The buttons write the same markers the site renders (**bold**, *italic*,
 * _underline_, [label](url), "- " bullets), so what the preview shows is
 * what the page will show.
 */

import { useRef, type ReactNode } from 'react';
import { useT } from '@/lib/i18n';
import { T } from '@/components/T';
import { renderRichText } from '@/lib/richText';

interface RichTextFieldProps {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}

export default function RichTextField({ label, value, placeholder, onChange }: RichTextFieldProps) {
  const t = useT();
  const ref = useRef<HTMLTextAreaElement>(null);

  /** Wrap the selection, or drop the markers in place for the cursor. */
  const wrap = (before: string, after: string, sample: string) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end) || sample;
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + before.length, start + before.length + selected.length);
    });
  };

  /** Turn the lines the selection touches into bullets. */
  const bullet = () => {
    const el = ref.current;
    if (!el) return;
    const start = value.lastIndexOf('\n', Math.max(0, el.selectionStart - 1)) + 1;
    const endOfLine = value.indexOf('\n', el.selectionEnd);
    const end = endOfLine === -1 ? value.length : endOfLine;
    const block = value.slice(start, end)
      .split('\n')
      .map((line) => (line.match(/^\s*-\s+/) ? line : `- ${line}`))
      .join('\n');
    onChange(value.slice(0, start) + block + value.slice(end));
    requestAnimationFrame(() => el.focus());
  };

  const buttons: Array<{ id: string; icon: string; title: string; run: () => void }> = [
    { id: 'bold', icon: 'fa-bold', title: t('textEditor.bold'), run: () => wrap('**', '**', t('textEditor.sampleBold')) },
    { id: 'italic', icon: 'fa-italic', title: t('textEditor.italic'), run: () => wrap('*', '*', t('textEditor.sampleItalic')) },
    { id: 'underline', icon: 'fa-underline', title: t('textEditor.underline'), run: () => wrap('_', '_', t('textEditor.sampleUnderline')) },
    { id: 'link', icon: 'fa-link', title: t('textEditor.link'), run: () => wrap('[', '](https://artrouting.com)', t('textEditor.sampleLink')) },
    { id: 'bullet', icon: 'fa-list-ul', title: t('textEditor.bullet'), run: bullet },
    { id: 'break', icon: 'fa-turn-down', title: t('textEditor.lineBreak'), run: () => wrap('\n', '', '') },
  ];

  const preview: ReactNode = value.trim() ? renderRichText(value) : t('textEditor.previewEmpty');

  return (
    <div className="artext-field">
      <label className="artext-field-label">{label}</label>

      <div className="artext-toolbar" role="toolbar" aria-label={t('textEditor.toolbar')}>
        {buttons.map((b) => (
          <button
            key={b.id}
            type="button"
            className="artext-toolbar-btn"
            title={b.title}
            aria-label={b.title}
            onClick={b.run}
          >
            <i className={`fas ${b.icon}`} />
          </button>
        ))}
      </div>

      <textarea
        ref={ref}
        className="artext-textarea"
        rows={4}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />

      <div className="artext-preview">
        <span className="artext-preview-label"><T k="textEditor.preview" /></span>
        <div className="artext-preview-body">{preview}</div>
      </div>
    </div>
  );
}
